<template>
  <div>
    <div class="calculator-container" :class="{ 'remove-margin': removeMargin }">
      <h1 class="h3">
        Linear Regression Calculator
      </h1>
      <div class="inputs-container">
        <div>
          <div class="label-tooltip">
            <label class="type-data">
              Column X Variable Name (Optional)
            </label>
            <div class="icon-info-tooltip linear-regression">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter.</span>
              </Tooltip>
            </div>
          </div>
          <input type="text">
        </div>
        <div>
          <div class="label-tooltip">
            <label class="type-data">
              Column Y Variable Name (Optional)
            </label>
            <div class="icon-info-tooltip linear-regression">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter.</span>
              </Tooltip>
            </div>
          </div>
          <input type="text">
        </div>
      </div>
      <form @submit.prevent="calculations">
        <div class="row-data-container">
          <div class="raw-data">
            <div class="label-tooltip">
              <label class="type-data">
                Column X Values
              </label>
              <div class="icon-info-tooltip linear-regression">
                <IconInfo />
                <Tooltip>
                  <span>Input the data separated by comma, space or enter.</span>
                </Tooltip>
              </div>
            </div>
            <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
              <textarea
                v-model="rawDataMean"
                cols="30" rows="10"
                placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
              />
            </div>
            <div v-if="textareaAlert" class="error">
              {{ textareaAlert }}
            </div>
          </div>
          <div class="raw-data">
            <div class="label-tooltip">
              <label class="type-data">
                Column Y Values
              </label>
              <div class="icon-info-tooltip linear-regression">
                <IconInfo />
                <Tooltip>
                  <span>Input the data separated by comma, space or enter.</span>
                </Tooltip>
              </div>
            </div>
            <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
              <textarea
                v-model="rawDataMean"
                cols="30" rows="10"
                placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
              />
            </div>
            <div v-if="textareaAlert" class="error">
              {{ textareaAlert }}
            </div>
          </div>
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div
      v-show="linearRegression" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div :key="symbols[0]" class="results">
        <div>
          <span class="result-name">Number of observations:</span>
          <span class="result-value">n = {{ size }}</span>
        </div>
        <div>
          <span class="result-name"><span class="capitalize-first-letter">{{ selectedCalculationMethod }}</span> Standard Deviation:</span>
          <span class="result-value"><span>\[{{ symbols[0] }}\]</span> = {{ standardDeviation }}</span>
        </div>
        <div>
          <span class="result-name">Mean:</span>
          <span class="result-value"><span>\[\{{ symbols[1] }}\]</span> = {{ mean }}</span>
        </div>
        <div>
          <span class="result-name">Sum of Squares:</span>
          <span class="result-value">SS = {{ sumOfSquares }}</span>
        </div>
        <div>
          <span class="result-name">Coefficient of Variation (CV):</span>
          <span class="result-value">CV = {{ linearRegression }}</span>
        </div>
        <div>
          <span class="result-name">Coefficient of Variation (CV) %:</span>
          <span class="result-value">CV(%) = {{ coefficientOfVariationPercentage }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';

export default {
  components: {
    Dropdown, Tooltip, IconInfo,
  },
  data() {
    return {
      linearRegression: 0,
      coefficientOfVariationPercentage: 0,
      mean: 0,
      size: 0,
      standardDeviation: 0,
      variance: 0,
      sumOfSquares: 0,
      meanSubtracted: [],
      meanSubtractedSquared: [],
      rawDataMeanArray: [],
      rawDataMean: '',
      selectedCalculationMethod: 'sample',
      textareaAlert: false,
      selectedDecimalPlaces: '4',
      removeMargin: false,
      resetProp: false,
      resultNumber: 0,
    };
  },
  computed: {
    hideResultVariablesArray() {
      return [
        this.rawDataMean,
        this.selectedDecimalPlaces,
      ];
    },
    symbols() {
      return this.selectedCalculationMethod === 'sample' ? ['s', 'bar{X}'] : ['\\sigma', 'mu'];
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  mounted() {
    this.$store.commit('calculators/result', false);
  },
  methods: {
    currentSelectedCalculationMethod(currentMethod) {
      this.selectedCalculationMethod = currentMethod;
    },
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'summary';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    reset(e) {
      this.selectedCalculationMethod = 'sample';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.resetProp = true;
      this.rawDataMean = '';
      this.selectedDecimalPlaces = '4';
      this.linearRegression = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    hideResults() {
      this.removeMargin = false;
      this.linearRegression = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    loadExample(e) {
      this.selectedDecimalPlaces = '4';
      this.rawDataMean = '14\n25\n35\n37\n75\n52\n59';
      this.hideResults();

      e.preventDefault();
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    inputValidation() {
      this.textareaAlert = '';
      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        return false;
      }

      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
          this.textareaAlert = 'Invalid value';
          return false;
        }
      }

      if (this.rawDataMeanArray.length < 2 && this.rawDataMeanArray.length > 0) {
        this.textareaAlert = 'To calculate coefficient of variation, you need at least two values in your dataset.';

        return false;
      }

      return true;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      this.mean = 0;
      this.size = this.rawDataMeanArray.length;
      this.standardDeviation = 0;
      this.sumOfSquares = 0;
      this.meanSubtracted = [];
      this.meanSubtractedSquared = [];
      const zeroOrOne = this.selectedCalculationMethod === 'sample' ? 1 : 0;

      // Calculating the mean
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        this.mean += Number(this.rawDataMeanArray[i]);
      }

      this.mean /= this.size;

      // Subtract mean from each input value
      this.meanSubtracted = this.rawDataMeanArray.map((num) => num - this.mean);

      // Square the result of the subtraction
      this.meanSubtractedSquared = this.meanSubtracted.map((num) => num ** 2);

      // Calculating the sum of squares
      this.sumOfSquares = this.meanSubtractedSquared.reduce((a, b) => a + b, 0);

      // Calculating the variance
      this.variance = this.sumOfSquares / (this.size - zeroOrOne);

      // Calculating the standard deviation
      this.standardDeviation = Math.sqrt(this.variance);

      // Calculating the coefficient of variation
      this.linearRegression = this.standardDeviation / this.mean;

      // Transform COV to percentage
      this.coefficientOfVariationPercentage = this.linearRegression * 100;

      this.removeMargin = true;
      this.checkAndFormatResult();
      this.calculationData();
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.scrollToSection();
    },
    checkAndFormatResult() {
      const onlyZerosTest = /^\d+.[0]+$/;
      const resultsArray = [
        ['standardDeviation', this.standardDeviation.toString()],
        ['linearRegression', this.linearRegression.toString()],
        ['coefficientOfVariationPercentage', this.coefficientOfVariationPercentage.toString()],
        ['variance', this.variance.toString()],
        ['mean', this.mean.toString()],
        ['sumOfSquares', this.sumOfSquares.toString()],
        ['meanSubtracted', this.meanSubtracted[0].toString()],
        ['meanSubtractedSquared', this.meanSubtractedSquared[0].toString()],
      ];

      for (let i = 0; i < resultsArray.length; i += 1) {
        if (resultsArray[i][1].split('').includes('e')) {
          [resultsArray[i][1]] = resultsArray[i][1].split('e');
        }

        if (onlyZerosTest.test(resultsArray[i][1])) {
          resultsArray[i][1] = Math.trunc(resultsArray[i][1]).toString();
        }

        this[resultsArray[i][0]] = Number(resultsArray[i][1]).toFixed(this.selectedDecimalPlaces);
      }
    },
    calculationData() {
      this.$store.commit('calculators/calculationData',
        {
          rawDataMeanArray: this.rawDataMeanArray,
          method: this.selectedCalculationMethod,
          mean: this.mean,
          standardDeviation: this.standardDeviation,
          selectedDecimalPlaces: this.selectedDecimalPlaces,
          sumOfSquares: this.sumOfSquares,
          meanSubtractedFirstValue: this.meanSubtracted,
          meanSubtractedSquaredFirstValue: this.meanSubtractedSquared,
          size: this.size,
          linearRegression: this.linearRegression,
          variance: this.variance,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .decimal-places {
    width: calc(50% - 15px);
    margin-top: var(--spacing-30);
    margin-bottom: var(--spacing-36);

    .dropdown::v-deep {
      &.open {
        .list {
          width: 100.63%;
          left: -1px;
        }
      }
    }
  }

  .label-tooltip .icon-info-tooltip {
    margin: 0;
  }

  .inputs-container {
    display: flex;
    margin-bottom: 17px;
    gap: var(--spacing-30);
  }

  .row-data-container {
    display: flex;
    margin-bottom: var(--spacing-36);
    gap: var(--spacing-24);

    .raw-data {
      margin-top: 0;

      .label-tooltip .icon-info-tooltip {
        &.linearRegression {
          &:hover {
            .tooltip {
              height: auto;
            }
          }
        }
      }
    }
  }

  .label-tooltip.your-data-tooltip {
    .your-data {
      margin-bottom: 0;
    }

    .icon-info-tooltip {
      &.linearRegression {
        &:hover {
          .tooltip {
            height: auto;
            top: 28px;
            bottom: unset;

            &:after {
              top: -11px;
              rotate: 180deg;
            }
          }
        }
      }
    }
  }
}

.summary-container {
  min-height: unset !important;

  .results {
    min-height: unset !important;

    div {
      justify-content: space-between;
      padding: 0 var(--spacing-80) 0 10px;

      &:nth-child(even) {
        background: rgba(218, 246, 251, 0.55);
      }

      span.result-name {
        width: unset;
      }

      .result-value {
        span {
          ::v-deep .MathJax_SVG_Display {
            display: unset !important;
            margin: 0;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    &.remove-margin {
      margin-bottom: 0;
    }

    .decimal-places {
      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip {
              left: 130%;

              &:after {
                left: 42.15%;
              }
            }
          }
        }
      }
    }
  }

  .label-tooltip.your-data-tooltip .icon-info-tooltip {
    &.linear-regression {
      &:hover {
        .tooltip {
          left: 18px;

          &:after {
            left: 43.11%;
          }
        }
      }
    }
  }

  div .summary-container {
    margin-top: var(--spacing-60);
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      width: 100%;

      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip {
              left: 271%;

              &:after {
                left: 34.5%;
              }
            }
          }
        }
      }
    }

    .label-tooltip.your-data-tooltip .icon-info-tooltip {
      &.linear-regression {
        &:hover {
          .tooltip {
            left: 40px;

            &:after {
              left: 35.21%;
            }
          }
        }
      }
    }
  }

  .summary-container {
    div {
      overflow-y: hidden;

      .result-value {
        white-space: nowrap;
      }
    }
  }

  div .interpretation-container {
    padding: var(--spacing-32) var(--spacing-20);
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
